<script context="module" lang="ts">
  enum FeatureFlagTab {
    FlagDetails,
    Override,
  }
</script>

<script lang="ts">
  import { onMount } from 'svelte';
  import { getDrawerStore, getToastStore, Tab, TabGroup } from '@skeletonlabs/skeleton';
  import { featureFlagStore } from '$lib/stores/featureFlagStore';
  import type { AdminClientOverride, GetAliiFeatureByIdResponse, PutAliiFeatureRequest } from '$lib/models/alii-features/types';
  import type { ErrorResponse } from '$lib/models/types';
  import { getErrorMessage, isErrorResponse } from '$lib/utils/error/errorUtils';
  import type { DataHandler } from '@vincjo/datatables/remote';
  import { FlagBanner, ListDashes, Plugs } from 'phosphor-svelte';
  import IconButton from '$lib/components/common/iconButton/IconButton.svelte';
  import Details from '$lib/components/drawers/featureFlag/tabs/Details.svelte';
  import Override from '$lib/components/drawers/featureFlag/tabs/Override.svelte';
  import Button from '$lib/components/common/button/Button.svelte';

  export let name: string;
  export let dataHandler: DataHandler;

  const toastrStore = getToastStore();
  let loading: boolean = true;
  let description: string = '';
  let active: boolean;
  let isAdminUser: boolean = false;
  let clientOverrides: AdminClientOverride[] = [];
  let savedClientOverrides: AdminClientOverride[] = [];
  let tabSet: number = FeatureFlagTab.FlagDetails;

  const drawerStore = getDrawerStore();

  onMount(async () => {
    try {
      const result: GetAliiFeatureByIdResponse | ErrorResponse = await featureFlagStore.fetchByName(name);
      if (isErrorResponse(result)) {
        toastrStore.trigger({
          message: getErrorMessage(result),
          background: 'bg-error-500',
        });
      } else {
        const data = result as GetAliiFeatureByIdResponse;
        active = data.isGloballyEnabled;
        name = data.featureName;
        description = data.description;
        isAdminUser = data.isAdminUser;
        clientOverrides = data.clientOverrides;
        savedClientOverrides = [...data.clientOverrides];
      }
    } catch (err) {
    } finally {
      loading = false;
    }
  });

  function onCloseClick() {
    drawerStore.close();
  }

  async function onSave(): Promise<void> {
    const request: PutAliiFeatureRequest = {
      name: name,
      isActive: active,
      description: description,
      bridgeClientAliiFeatures: clientOverrides.map((co) => ({
        id: undefined,
        clientName: '',
        clientId: co.clientId,
        isActive: co.isActive,
      })),
    };

    await featureFlagStore
      .update(request)
      .catch(() => {
        toastrStore.trigger({
          message: 'Failed to update Alii Toggle',
          background: 'bg-error-500',
        });
      })
      .then(() => {
        drawerStore.close();
        dataHandler.invalidate();
      });
  }
</script>

<div class="card flex h-full flex-col">
  <header class="flex justify-between border-b p-5">
    <div class="inline-flex items-center gap-2 p-4 text-2xl font-bold">
      <span>
        <FlagBanner />
      </span>
      <span> Edit Feature Flag </span>
    </div>
    <div class="content-center">
      <IconButton icon="x" onClick={onCloseClick} />
    </div>
  </header>
  <div class="flex-grow overflow-y-auto p-4">
    {#if loading}
      <p>Loading...</p>
    {:else}
      <div class="p-5">
        <div class="mb-5 text-xl font-bold text-primary-500">
          {name}
        </div>
        <div>
          <TabGroup border="border-b">
            <Tab bind:group={tabSet} name="flagDetails" value={FeatureFlagTab.FlagDetails}>
              <div class="flex items-center gap-1">
                <span><ListDashes /></span> <span>Flag Details</span>
              </div>
            </Tab>
            <Tab bind:group={tabSet} name="override" value={FeatureFlagTab.Override}>
              <div class="flex items-center gap-1">
                <span><Plugs /></span> <span>Override</span>
              </div>
            </Tab>
            <svelte:fragment slot="panel">
              {#if tabSet === FeatureFlagTab.FlagDetails}
                <Details {name} bind:description bind:enabled={active} {isAdminUser} />
              {:else if tabSet === FeatureFlagTab.Override}
                <Override {name} {savedClientOverrides} bind:clientOverrides />
              {/if}
            </svelte:fragment>
          </TabGroup>
        </div>
      </div>
    {/if}
  </div>
  <footer class="flex w-full justify-between gap-5 border-t p-4">
    <Button label="Cancel" size="large" variant="outlined" onClick={onCloseClick} />
    <div class="inline-flex flex-wrap gap-5">
      <Button label="Save Changes" colour="primary" size="large" onClick={() => onSave()} />
    </div>
  </footer>
</div>
